// French Payroll Engine - implements the abstract PayrollEngine for French compliance

import { supabase } from '@/integrations/supabase/client';
import { Employee } from '@/types';
import { PayrollEngine } from '../core/PayrollEngine';
import {
  CountryPayrollConstants,
  PayrollInputData,
  GrossPayComponents,
  InsuranceCalculation,
  TaxCalculation,
  DeductionCalculation,
  ValidationError,
  CalculationError
} from '../core/types';

// French-specific types
export type FrenchContractType = 'CDI' | 'CDD' | 'STAGE' | 'APPRENTI' | 'INTERIM';

interface FrenchPayrollConstants {
  year: number;
  country_code: string;
  
  // Minimum wage (SMIC)
  smic_hourly: number; // €11.52 in 2024
  smic_monthly: number; // €1,766.92 in 2024
  
  // Tax system (income tax brackets for 2024)
  tax_bracket_0_percent_limit: number; // €11,294
  tax_bracket_11_percent_limit: number; // €28,797
  tax_bracket_30_percent_limit: number; // €82,341
  tax_bracket_41_percent_limit: number; // €177,106
  tax_bracket_45_percent: number; // Above €177,106
  
  // Social security rates (Sécurité Sociale) - 2024
  ss_health_rate_employee: number; // 0.0% (abolished in 2018)
  ss_health_rate_employer: number; // 13.00%
  
  ss_pension_basic_rate_employee: number; // 6.90% (under ceiling)
  ss_pension_basic_rate_employer: number; // 8.55% (under ceiling)
  
  ss_pension_complementary_rate_employee: number; // 3.15% (AGIRC-ARRCO)
  ss_pension_complementary_rate_employer: number; // 4.72% (AGIRC-ARRCO)
  
  ss_unemployment_rate_employee: number; // 2.40%
  ss_unemployment_rate_employer: number; // 4.05%
  
  ss_family_rate_employee: number; // 0.0%
  ss_family_rate_employer: number; // 5.25%
  
  ss_accident_rate_employer: number; // Variable by company (0.7% average)
  
  // CSG/CRDS (General Social Contribution)
  csg_rate_deductible: number; // 6.80% (deductible)
  csg_rate_non_deductible: number; // 2.40% (non-deductible)
  crds_rate: number; // 0.50%
  
  // Social security ceiling (Plafond Sécurité Sociale)
  ss_ceiling_monthly: number; // €3,864 in 2024
  ss_ceiling_annual: number; // €46,368 in 2024
  
  // Working time limits
  legal_working_time_weekly: number; // 35 hours
  overtime_threshold_weekly: number; // 35 hours
  
  // Overtime rates
  overtime_rate_25_percent: number; // First 8 hours (36-43h)
  overtime_rate_50_percent: number; // Beyond 43h
  
  // Meal vouchers (Tickets Restaurant)
  meal_voucher_max_value: number; // €8.00 max value
  meal_voucher_employer_max_contribution: number; // €5.96 max employer contribution
  
  // Transport subsidy
  transport_subsidy_rate: number; // 50% of public transport cost
  transport_subsidy_max: number; // No legal maximum
  
  // Professional tax (Taxe Professionnelle) - replaced by CET
  cet_rate: number; // Variable by location
  
  // Employment contribution rates
  formation_rate_employer: number; // 0.55% (training)
  housing_contribution_rate_employer: number; // 0.45% (construction)
  transport_versement_rate_employer: number; // Variable by location (0.5-2.95%)
}

/**
 * French Payroll Calculation Engine
 * Implements French-specific payroll calculations including taxes,
 * social security contributions, and employment classifications
 */
export class FrenchPayrollEngine extends PayrollEngine {
  private employee!: Employee;
  private frenchConstants!: FrenchPayrollConstants;
  private year!: number;

  constructor(countryCode: string = 'FR', debugMode: boolean = false) {
    super(countryCode, debugMode);
  }

  /**
   * Load French payroll constants from database
   */
  protected async loadPayrollConstants(year: number): Promise<CountryPayrollConstants> {
    this.log(`Loading French payroll constants for year ${year}`);
    
    const { data, error } = await supabase
      .from('payroll_constants')
      .select('*')
      .eq('year', year)
      .eq('country_code', 'FR')
      .single();

    if (error || !data) {
      this.frenchConstants = this.createDefaultFrenchConstants(year);
      this.log('Using default French constants (database constants not found)');
    } else {
      this.frenchConstants = data as FrenchPayrollConstants;
    }
    
    this.year = year;

    return {
      countryCode: 'FR',
      year,
      currency: 'EUR',
      minimumWageHourly: this.frenchConstants.smic_hourly,
      minimumWageMonthly: this.frenchConstants.smic_monthly,
      minimumWageAnnual: this.frenchConstants.smic_monthly * 12,
      standardHoursPerWeek: this.frenchConstants.legal_working_time_weekly,
      maxHoursPerWeek: 48, // EU directive limit
      maxHoursPerDay: 10,
      taxSystem: 'PROGRESSIVE',
      taxBrackets: [
        { min: 0, max: this.frenchConstants.tax_bracket_0_percent_limit, rate: 0 },
        { min: this.frenchConstants.tax_bracket_0_percent_limit, max: this.frenchConstants.tax_bracket_11_percent_limit, rate: 0.11 },
        { min: this.frenchConstants.tax_bracket_11_percent_limit, max: this.frenchConstants.tax_bracket_30_percent_limit, rate: 0.30 },
        { min: this.frenchConstants.tax_bracket_30_percent_limit, max: this.frenchConstants.tax_bracket_41_percent_limit, rate: 0.41 },
        { min: this.frenchConstants.tax_bracket_41_percent_limit, max: null, rate: this.frenchConstants.tax_bracket_45_percent }
      ],
      personalAllowance: this.frenchConstants.tax_bracket_0_percent_limit,
      dependentAllowances: {
        child: 0 // France uses quotient familial system
      },
      socialInsuranceRates: {
        employee: {
          pension_basic: this.frenchConstants.ss_pension_basic_rate_employee,
          pension_complementary: this.frenchConstants.ss_pension_complementary_rate_employee,
          unemployment: this.frenchConstants.ss_unemployment_rate_employee,
          csg_deductible: this.frenchConstants.csg_rate_deductible,
          csg_non_deductible: this.frenchConstants.csg_rate_non_deductible,
          crds: this.frenchConstants.crds_rate
        },
        employer: {
          health: this.frenchConstants.ss_health_rate_employer,
          pension_basic: this.frenchConstants.ss_pension_basic_rate_employer,
          pension_complementary: this.frenchConstants.ss_pension_complementary_rate_employer,
          unemployment: this.frenchConstants.ss_unemployment_rate_employer,
          family: this.frenchConstants.ss_family_rate_employer,
          accident: this.frenchConstants.ss_accident_rate_employer,
          formation: this.frenchConstants.formation_rate_employer,
          housing: this.frenchConstants.housing_contribution_rate_employer
        }
      },
      healthInsuranceRates: {
        employee: this.frenchConstants.ss_health_rate_employee,
        employer: this.frenchConstants.ss_health_rate_employer
      },
      maxAssessmentBase: this.frenchConstants.ss_ceiling_monthly,
      minAssessmentBase: 0,
      countrySpecificConstants: {
        contractTypes: ['CDI', 'CDD', 'STAGE', 'APPRENTI', 'INTERIM'],
        socialSecurityCeiling: this.frenchConstants.ss_ceiling_monthly,
        overtimeThreshold: this.frenchConstants.overtime_threshold_weekly,
        overtimeRate25: this.frenchConstants.overtime_rate_25_percent,
        overtimeRate50: this.frenchConstants.overtime_rate_50_percent,
        mealVoucherMaxValue: this.frenchConstants.meal_voucher_max_value,
        transportSubsidyRate: this.frenchConstants.transport_subsidy_rate
      },
      validationRules: {
        mandatoryEmployeeFields: ['numero_securite_sociale', 'contract_type'],
        employeeFieldValidators: {
          numero_securite_sociale: { type: 'custom', customValidator: 'validateFrenchSSN' },
          contract_type: { type: 'string', pattern: '^(CDI|CDD|STAGE|APPRENTI|INTERIM)$' }
        },
        contractTypes: ['CDI', 'CDD', 'STAGE', 'APPRENTI', 'INTERIM'],
        payFrequencies: ['monthly']
      }
    };
  }

  /**
   * Calculate gross pay components for French employees
   */
  protected async calculateGrossComponents(
    employee: Employee,
    inputData: PayrollInputData
  ): Promise<GrossPayComponents> {
    this.log(`Calculating gross components for French employee ${employee.name}`);
    
    this.employee = employee;
    
    const hourlyRate = this.getHourlyRate(employee);
    this.log(`Using hourly rate: €${hourlyRate}/hour`);
    
    // Calculate regular pay
    const regularHours = Math.min(inputData.regularHours, this.frenchConstants.legal_working_time_weekly * 4.33);
    const basePay = regularHours * hourlyRate;
    
    // Calculate overtime with French rates
    const { overtimePay, overtimeHours } = this.calculateFrenchOvertime(
      inputData.regularHours, 
      inputData.overtimeHours, 
      hourlyRate
    );
    
    // Special pay (French-specific)
    const nightPay = (inputData.specialHours.night || 0) * hourlyRate * 0.10; // 10% night premium
    const sundayPay = (inputData.specialHours.sunday || 0) * hourlyRate * 1.00; // 100% Sunday premium
    const holidayPay = (inputData.specialHours.holiday || 0) * hourlyRate * 1.00;
    
    // Paid leave calculation
    const vacationPay = inputData.vacationDays * hourlyRate * 7; // 7-hour average workday
    const sickPay = inputData.sickLeaveDays * hourlyRate * 7; // Social security covers part
    
    const specialPay = {
      night: nightPay,
      sunday: sundayPay,
      holiday: holidayPay
    };
    
    const absencePay = {
      vacation: vacationPay,
      sickLeave: sickPay
    };
    
    const total = basePay + overtimePay + nightPay + sundayPay + holidayPay + 
                 vacationPay + sickPay + inputData.bonuses + inputData.otherIncome;
    
    this.log(`French gross components: Base €${basePay}, OT €${overtimePay}, Total €${total}`);
    
    return {
      basePay,
      overtimePay,
      specialPay,
      absencePay,
      bonuses: inputData.bonuses,
      otherIncome: inputData.otherIncome,
      total
    };
  }

  /**
   * Calculate French social security contributions
   */
  protected async calculateInsuranceContributions(
    employee: Employee,
    grossComponents: GrossPayComponents,
    inputData: PayrollInputData
  ): Promise<Record<string, InsuranceCalculation>> {
    this.log(`Calculating French social security contributions`);
    
    const grossSalary = grossComponents.total;
    const contractType = employee.contract_type as FrenchContractType;
    
    // Handle special cases for apprentices and interns
    if (contractType === 'APPRENTI') {
      return this.calculateApprenticeInsurance(grossSalary);
    }
    
    if (contractType === 'STAGE') {
      return this.calculateInternInsurance(grossSalary);
    }
    
    // Standard employee contributions
    return this.calculateStandardFrenchInsurance(grossSalary);
  }

  /**
   * Calculate French income tax
   */
  protected async calculateTax(
    employee: Employee,
    grossComponents: GrossPayComponents,
    insuranceCalculations: Record<string, InsuranceCalculation>,
    inputData: PayrollInputData
  ): Promise<TaxCalculation> {
    this.log(`Calculating French tax`);
    
    // Get deductible CSG
    const deductibleCSG = insuranceCalculations.csg?.employee?.deductible || 0;
    
    // Tax base = gross - deductible social contributions
    const taxBase = grossComponents.total - deductibleCSG;
    
    // Apply personal allowance
    const taxableIncome = Math.max(0, taxBase - this.frenchConstants.tax_bracket_0_percent_limit);
    
    // Calculate income tax (simplified - actual calculation considers quotient familial)
    const incomeTax = this.calculateFrenchIncomeTax(taxableIncome);
    
    return {
      taxBase,
      taxableIncome,
      allowances: {
        basic: this.frenchConstants.tax_bracket_0_percent_limit
      },
      taxBrackets: {
        incomeTax
      },
      credits: {},
      totalTax: incomeTax,
      netTaxBenefit: 0
    };
  }

  /**
   * Calculate other French deductions
   */
  protected async calculateOtherDeductions(
    employee: Employee,
    grossComponents: GrossPayComponents,
    inputData: PayrollInputData
  ): Promise<DeductionCalculation[]> {
    this.log(`Calculating French other deductions`);
    
    const deductions: DeductionCalculation[] = [];
    
    // Meal vouchers (employer benefit)
    const mealVoucherCount = inputData.otherDeductions?.mealVouchers || 0;
    if (mealVoucherCount > 0) {
      const employerContribution = Math.min(
        mealVoucherCount * this.frenchConstants.meal_voucher_employer_max_contribution,
        mealVoucherCount * this.frenchConstants.meal_voucher_max_value * 0.60
      );
      
      // This is actually a benefit, not a deduction, but tracked here for completeness
      deductions.push({
        type: 'OTHER',
        amount: -employerContribution, // Negative = benefit
        description: 'Meal Voucher Employer Contribution (Tickets Restaurant)',
        mandatory: false
      });
    }
    
    // Transport subsidy (50% of public transport costs)
    const transportCost = inputData.otherDeductions?.transportCost || 0;
    if (transportCost > 0) {
      const subsidyAmount = transportCost * this.frenchConstants.transport_subsidy_rate;
      deductions.push({
        type: 'OTHER',
        amount: -subsidyAmount, // Negative = benefit
        description: 'Transport Subsidy (Remboursement Transport)',
        mandatory: true
      });
    }
    
    // Advance payments
    if (inputData.advances > 0) {
      deductions.push({
        type: 'OTHER',
        amount: inputData.advances,
        description: 'Advance Payments (Avances sur Salaire)',
        mandatory: true
      });
    }
    
    // Other deductions
    Object.entries(inputData.otherDeductions || {}).forEach(([key, amount]) => {
      if (amount > 0 && key !== 'mealVouchers' && key !== 'transportCost') {
        deductions.push({
          type: 'OTHER',
          amount,
          description: key,
          mandatory: false
        });
      }
    });
    
    return deductions;
  }

  /**
   * French-specific validation
   */
  protected async validateCountrySpecificData(
    employee: Employee,
    inputData: PayrollInputData
  ): Promise<Record<string, string[]>> {
    const errors: Record<string, string[]> = {};
    
    // Validate contract type
    if (!employee.contract_type || !['CDI', 'CDD', 'STAGE', 'APPRENTI', 'INTERIM'].includes(employee.contract_type)) {
      errors.contract_type = ['Invalid or missing contract type for French employee'];
    }
    
    // Validate Social Security Number
    if (employee.numero_securite_sociale && !this.validateFrenchSSN(employee.numero_securite_sociale)) {
      errors.numero_securite_sociale = ['Invalid French social security number format'];
    }
    
    // Validate SMIC compliance
    const hourlyRate = this.getHourlyRate(employee);
    if (hourlyRate < this.frenchConstants.smic_hourly) {
      errors.hourly_rate = [`Hourly rate cannot be below SMIC (€${this.frenchConstants.smic_hourly})`];
    }
    
    // Validate working time compliance
    const weeklyHours = inputData.regularHours + inputData.overtimeHours;
    if (weeklyHours > 48) {
      errors.working_hours = ['Weekly working hours cannot exceed 48 hours (EU directive)'];
    }
    
    return errors;
  }

  /**
   * Helper methods
   */
  
  private createDefaultFrenchConstants(year: number): FrenchPayrollConstants {
    return {
      year,
      country_code: 'FR',
      smic_hourly: 11.52,
      smic_monthly: 1766.92,
      tax_bracket_0_percent_limit: 11294,
      tax_bracket_11_percent_limit: 28797,
      tax_bracket_30_percent_limit: 82341,
      tax_bracket_41_percent_limit: 177106,
      tax_bracket_45_percent: 0.45,
      ss_health_rate_employee: 0.0,
      ss_health_rate_employer: 0.13,
      ss_pension_basic_rate_employee: 0.069,
      ss_pension_basic_rate_employer: 0.0855,
      ss_pension_complementary_rate_employee: 0.0315,
      ss_pension_complementary_rate_employer: 0.0472,
      ss_unemployment_rate_employee: 0.024,
      ss_unemployment_rate_employer: 0.0405,
      ss_family_rate_employee: 0.0,
      ss_family_rate_employer: 0.0525,
      ss_accident_rate_employer: 0.007,
      csg_rate_deductible: 0.068,
      csg_rate_non_deductible: 0.024,
      crds_rate: 0.005,
      ss_ceiling_monthly: 3864,
      ss_ceiling_annual: 46368,
      legal_working_time_weekly: 35,
      overtime_threshold_weekly: 35,
      overtime_rate_25_percent: 0.25,
      overtime_rate_50_percent: 0.50,
      meal_voucher_max_value: 8.00,
      meal_voucher_employer_max_contribution: 5.96,
      transport_subsidy_rate: 0.50,
      transport_subsidy_max: 9999999, // No legal maximum
      cet_rate: 0.002, // Variable by location
      formation_rate_employer: 0.0055,
      housing_contribution_rate_employer: 0.0045,
      transport_versement_rate_employer: 0.005 // Average rate
    };
  }

  private calculateFrenchOvertime(regularHours: number, overtimeHours: number, hourlyRate: number): { overtimePay: number; overtimeHours: number } {
    const weeklyHours = regularHours + overtimeHours;
    const overtimeThreshold = this.frenchConstants.legal_working_time_weekly;
    
    if (weeklyHours <= overtimeThreshold) {
      return { overtimePay: 0, overtimeHours: 0 };
    }
    
    const totalOvertimeHours = weeklyHours - overtimeThreshold;
    let overtimePay = 0;
    
    // First 8 hours of overtime (25% premium)
    const firstTierHours = Math.min(totalOvertimeHours, 8);
    overtimePay += firstTierHours * hourlyRate * (1 + this.frenchConstants.overtime_rate_25_percent);
    
    // Additional overtime hours (50% premium)
    const secondTierHours = Math.max(0, totalOvertimeHours - 8);
    overtimePay += secondTierHours * hourlyRate * (1 + this.frenchConstants.overtime_rate_50_percent);
    
    return { overtimePay, overtimeHours: totalOvertimeHours };
  }

  private calculateStandardFrenchInsurance(grossSalary: number): Record<string, InsuranceCalculation> {
    const ceilingAmount = Math.min(grossSalary, this.frenchConstants.ss_ceiling_monthly);
    
    return {
      pension_basic: {
        employee: { main: ceilingAmount * this.frenchConstants.ss_pension_basic_rate_employee },
        employer: { main: ceilingAmount * this.frenchConstants.ss_pension_basic_rate_employer },
        employeeTotal: ceilingAmount * this.frenchConstants.ss_pension_basic_rate_employee,
        employerTotal: ceilingAmount * this.frenchConstants.ss_pension_basic_rate_employer
      },
      pension_complementary: {
        employee: { main: grossSalary * this.frenchConstants.ss_pension_complementary_rate_employee },
        employer: { main: grossSalary * this.frenchConstants.ss_pension_complementary_rate_employer },
        employeeTotal: grossSalary * this.frenchConstants.ss_pension_complementary_rate_employee,
        employerTotal: grossSalary * this.frenchConstants.ss_pension_complementary_rate_employer
      },
      unemployment: {
        employee: { main: ceilingAmount * this.frenchConstants.ss_unemployment_rate_employee },
        employer: { main: ceilingAmount * this.frenchConstants.ss_unemployment_rate_employer },
        employeeTotal: ceilingAmount * this.frenchConstants.ss_unemployment_rate_employee,
        employerTotal: ceilingAmount * this.frenchConstants.ss_unemployment_rate_employer
      },
      health: {
        employee: { main: 0 }, // No employee health contributions
        employer: { main: grossSalary * this.frenchConstants.ss_health_rate_employer },
        employeeTotal: 0,
        employerTotal: grossSalary * this.frenchConstants.ss_health_rate_employer
      },
      family: {
        employee: { main: 0 }, // Employer-only
        employer: { main: grossSalary * this.frenchConstants.ss_family_rate_employer },
        employeeTotal: 0,
        employerTotal: grossSalary * this.frenchConstants.ss_family_rate_employer
      },
      csg: {
        employee: { 
          deductible: grossSalary * this.frenchConstants.csg_rate_deductible,
          non_deductible: grossSalary * this.frenchConstants.csg_rate_non_deductible
        },
        employer: { main: 0 },
        employeeTotal: grossSalary * (this.frenchConstants.csg_rate_deductible + this.frenchConstants.csg_rate_non_deductible),
        employerTotal: 0
      },
      crds: {
        employee: { main: grossSalary * this.frenchConstants.crds_rate },
        employer: { main: 0 },
        employeeTotal: grossSalary * this.frenchConstants.crds_rate,
        employerTotal: 0
      }
    };
  }

  private calculateApprenticeInsurance(grossSalary: number): Record<string, InsuranceCalculation> {
    // Apprentices have reduced social security contributions
    return {
      pension_basic: {
        employee: { main: 0 }, // Exempt below SMIC
        employer: { main: 0 }, // Reduced rates for apprentices
        employeeTotal: 0,
        employerTotal: 0
      },
      health: {
        employee: { main: 0 },
        employer: { main: grossSalary * 0.07 }, // Reduced rate
        employeeTotal: 0,
        employerTotal: grossSalary * 0.07
      },
      unemployment: {
        employee: { main: 0 },
        employer: { main: 0 }, // Exempt for apprentices
        employeeTotal: 0,
        employerTotal: 0
      }
    };
  }

  private calculateInternInsurance(grossSalary: number): Record<string, InsuranceCalculation> {
    // Interns are generally exempt from most social security contributions
    // Only contributions above the internship allowance threshold
    const internshipThreshold = 4.35 * this.frenchConstants.smic_hourly * 35; // ~€600
    const contributionBase = Math.max(0, grossSalary - internshipThreshold);
    
    if (contributionBase <= 0) {
      return {
        pension_basic: { employee: { main: 0 }, employer: { main: 0 }, employeeTotal: 0, employerTotal: 0 },
        health: { employee: { main: 0 }, employer: { main: 0 }, employeeTotal: 0, employerTotal: 0 },
        unemployment: { employee: { main: 0 }, employer: { main: 0 }, employeeTotal: 0, employerTotal: 0 }
      };
    }
    
    return this.calculateStandardFrenchInsurance(contributionBase);
  }

  private calculateFrenchIncomeTax(taxableIncome: number): number {
    let tax = 0;
    let remainingIncome = taxableIncome;
    
    // French tax brackets
    const brackets = [
      { limit: this.frenchConstants.tax_bracket_11_percent_limit - this.frenchConstants.tax_bracket_0_percent_limit, rate: 0.11 },
      { limit: this.frenchConstants.tax_bracket_30_percent_limit - this.frenchConstants.tax_bracket_11_percent_limit, rate: 0.30 },
      { limit: this.frenchConstants.tax_bracket_41_percent_limit - this.frenchConstants.tax_bracket_30_percent_limit, rate: 0.41 }
    ];
    
    for (const bracket of brackets) {
      if (remainingIncome <= 0) break;
      
      const taxableAtThisRate = Math.min(remainingIncome, bracket.limit);
      tax += taxableAtThisRate * bracket.rate;
      remainingIncome -= taxableAtThisRate;
    }
    
    // Top bracket (45%)
    if (remainingIncome > 0) {
      tax += remainingIncome * this.frenchConstants.tax_bracket_45_percent;
    }
    
    return Math.round(tax * 100) / 100;
  }

  private validateFrenchSSN(ssn: string): boolean {
    // Basic validation for French Social Security Number (15 digits)
    // Format: 1 23 45 67 890 123 45 (13 digits + 2 key digits)
    const pattern = /^[12][0-9]{2}(0[1-9]|1[0-2])(0[1-9]|[12][0-9]|3[01])[0-9]{6}[0-9]{2}$/;
    const cleaned = ssn.replace(/\s/g, '');
    return pattern.test(cleaned) && cleaned.length === 15;
  }
}